import { createModal, StaticModal } from '@nexelem/modals';

import { FormProvider, useForm } from 'react-hook-form';
import { Button, Checkbox } from '@nexelem/components';
import { useTableTranslation } from '../../translations';
import { FormTextInput } from '@nexelem/form-components';
import { FormControlLabel } from '@nexelem/deprecated-theme';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSaveViewStore } from '../SaveViewStore';
import { v4 } from 'uuid';

const schema = z.object({
  name: z.string().min(1).max(35),
  description: z.string().max(80).optional(),
  isDefault: z.boolean(),
});

type SaveViewFormValues = z.infer<typeof schema>;

type SaveViewModalProps = {
  tableName: string;
  /** Encoded table state (`f` blob) to persist with the view. */
  data: string;
};

const SaveViewModal = ({ tableName, data }: SaveViewModalProps) => {
  const { t } = useTableTranslation();

  const saveView = useSaveViewStore((state) => state.saveView);
  const setDefaultView = useSaveViewStore((state) => state.setDefaultView);

  const form = useForm<SaveViewFormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = ({ name, description, isDefault }: SaveViewFormValues) => {
    if (!data) {
      return;
    }

    const viewId = v4();
    saveView(tableName, viewId, { name, description, data });

    if (isDefault) {
      setDefaultView(tableName, viewId);
    }

    closeSaveViewModal();
  };

  return (
    <FormProvider {...form}>
      <StaticModal component="form" maxWidth="lg" onClose={closeSaveViewModal} onSubmit={form.handleSubmit(onSubmit)}>
        <StaticModal.Header withCloseButton>{t('saveView')}</StaticModal.Header>
        <StaticModal.Content className="mb-4 flex flex-col gap-6">
          <div className="text-s18">{t('saveInfo')}</div>
          <FormTextInput
            required
            label={t('name')}
            maxLength={35}
            maxWidth={320}
            name="name"
            placeholder={t('namePlaceholder')}
          />
          <FormTextInput
            label={t('description')}
            maxLength={80}
            maxWidth={560}
            name="description"
            placeholder={t('descriptionPlaceholder')}
          />
          <FormControlLabel
            className="ml-4"
            control={<Checkbox {...form.register('isDefault')} />}
            label={<span className="ml-2 text-s16">{t('setAsDefaultView')}</span>}
          />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeSaveViewModal}>
            {t('cancel')}
          </Button>
          <Button type="submit">{t('save')}</Button>
        </StaticModal.Footer>
      </StaticModal>
    </FormProvider>
  );
};

export const { openSaveViewModal, closeSaveViewModal } = createModal(SaveViewModal, {
  name: 'SaveViewModal',
});
